import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  MapPin,
  Tag,
  Scissors,
  Layers,
  CircleCheck,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Charte de livraison ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Charte de livraison
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre charte de livraison de cartons à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Oubliez la corvée de charger des paquets de cartons volumineux
            dans votre voiture. Nous acheminons votre commande directement
            chez vous, avec des règles logistiques strictes.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Zéro intermédiaire",
              body: "Nous assurons le transport exclusivement avec notre propre société et nos propres chauffeurs en Île-de-France.",
              Icon: Truck,
            },
            {
              title: "Propreté et intégrité",
              body: "Vos cartons voyagent à l'abri des intempéries dans nos véhicules pour vous garantir un matériel sec, propre et prêt à l'emploi.",
              Icon: ShieldCheck,
            },
            {
              title: "Ponctualité et réactivité",
              body: "Une gestion locale qui nous permet de respecter nos créneaux pour ne jamais bloquer votre planning d'emballage.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Méthode pro en 4 étapes pédagogiques ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
            Méthode pro
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            La méthode pro : 4 étapes clés pour un carton parfait
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter la casse et gagner un temps précieux le jour J,
            suivez cette feuille de route simple.
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "L'écrémage",
              body: "Profitez-en pour faire le tri. Moins vous accumulez, plus vite vos cartons seront bouclés et faciles à charger.",
            },
            {
              n: "02",
              title: "La fondation",
              body: "Appliquez une bande adhésive sur la jonction centrale du fond du carton, puis croisez avec deux bandes perpendiculaires pour verrouiller la structure.",
            },
            {
              n: "03",
              title: "La répartition",
              body: "Ne surchargez jamais les grands volumes. Le lourd va dans les petits formats (livres, vaisselle) et le léger dans les grands (couettes, oreillers).",
            },
            {
              n: "04",
              title: "La signalétique",
              body: "Notez la pièce d'arrivée sur les côtés du carton plutôt que sur le dessus. C'est le seul moyen de lire l'information quand les boîtes sont empilées.",
            },
          ].map(({ n, title, body }) => (
            <li
              key={n}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 inline-flex items-center justify-center h-9 px-3 rounded-full bg-[#FF7A00] text-white font-display font-extrabold text-[13px] tracking-[0.1em]">
                ÉTAPE {n}
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[16px] text-[#0f1d2b]">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chatenay-malabry-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chatenay-malabry-cta-fermeture"
          >
            <Scissors className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs et pistolets
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Rôle crucial des adhésifs de scellage ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline" style={{ color: "#237cb5" }}>
              Adhésifs professionnels
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Le rôle crucial de nos adhésifs de scellage
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Mettre vos affaires dans une boîte solide ne suffit pas&nbsp;:
              le ruban adhésif est le véritable garant de la fermeture.
              Voici pourquoi nos accessoires font la différence.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/produit/rouleau-adhesif"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-chatenay-malabry-cta-adhesif"
              >
                <Tag className="h-4 w-4" strokeWidth={2.6} />
                Voir le rouleau d&apos;adhésif
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/produit/devidoir-pistolet-adhesif"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-chatenay-malabry-cta-pistolet"
              >
                <Scissors className="h-4 w-4" strokeWidth={2.6} />
                Pistolet à scotch
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-3">
            {[
              {
                title: "Une colle ultra-agressive",
                body: "Nos adhésifs professionnels adhèrent instantanément à la fibre du carton et ne se décollent pas, même sous la tension de rabats bombés ou chargés.",
              },
              {
                title: "Résistance aux déchirures",
                body: "Contrairement aux modèles bas de gamme qui se fendent en deux dans la longueur au moindre coup de cutter ou de tension, nos rubans supportent les manipulations brusques.",
              },
              {
                title: "Barrière hermétique",
                body: "Un scellage de qualité professionnelle bloque les infiltrations de poussière, d'humidité et d'air, préservant ainsi l'intégrité de vos textiles et documents délicats.",
              },
            ].map(({ title, body }) => (
              <div
                key={title}
                className="flex items-start gap-3 bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white shrink-0">
                  <CircleCheck className="h-4 w-4" strokeWidth={2.6} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chatenay-malabry-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
